import { hashFile } from "../../files/ChunkedFileReader";
import { inspectAmlogicImage, type AmlogicImageReport } from "../AmlogicImageParser";

export interface CachedAmlogicImageAnalysis {
  report: AmlogicImageReport;
  sha256?: string;
}

/** Shares immutable package analysis across every target in one browser tab. */
export class AmlogicImageCache {
  private readonly reports = new WeakMap<File, Promise<AmlogicImageReport>>();
  private readonly hashes = new WeakMap<File, Promise<string>>();

  constructor(
    private readonly inspect: (file: File) => Promise<AmlogicImageReport> = inspectAmlogicImage,
    private readonly hash: (file: File) => Promise<string> = hashFile,
  ) {}

  getReport(file: File): Promise<AmlogicImageReport> {
    const cached = this.reports.get(file);
    if (cached) return cached;
    const pending = this.inspect(file).catch((error) => {
      this.reports.delete(file);
      throw error;
    });
    this.reports.set(file, pending);
    return pending;
  }

  getSha256(file: File): Promise<string> {
    const cached = this.hashes.get(file);
    if (cached) return cached;
    const pending = this.hash(file).catch((error) => {
      this.hashes.delete(file);
      throw error;
    });
    this.hashes.set(file, pending);
    return pending;
  }

  async analyze(file: File, includeHash: boolean): Promise<CachedAmlogicImageAnalysis> {
    const report = await this.getReport(file);
    return {
      report,
      sha256: includeHash ? await this.getSha256(file) : undefined,
    };
  }
}
